import React, { useState, useRef, useEffect } from 'react';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { ChatHeader } from './ChatHeader';
import { AIChatState } from '@/types/aiChat';

interface ChatWindowProps {
  state: AIChatState;
  onClose: () => void;
  onMinimize: () => void;
  onSendMessage: (content: string) => Promise<void>;
  onClearError: () => void;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({
  state,
  onClose,
  onMinimize,
  onSendMessage,
  onClearError
}) => {
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // 自动滚动到底部
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [state.messages, state.isTyping, state.streamingContent]);

  // 处理发送消息
  const handleSendMessage = async (content: string) => {
    if (!content.trim() || state.isLoading) return;

    setInputValue('');
    await onSendMessage(content.trim());
  };

  // 处理错误关闭
  const handleCloseError = () => {
    onClearError();
  };

  return (
    <div className="w-96 h-[32rem] bg-white rounded-lg shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
      {/* 聊天窗口头部 */}
      <ChatHeader
        isOnline={!!state.session}
        onMinimize={onMinimize}
        onClose={onClose}
      />

      {/* 错误提示 */}
      {state.error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-3 m-2 rounded">
          <div className="flex justify-between items-start">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{state.error}</p>
              </div>
            </div>
            <button
              onClick={handleCloseError}
              className="flex-shrink-0 ml-3 text-red-400 hover:text-red-600"
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* 消息列表 */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
      >
        {state.messages.length === 0 && !state.isLoading && (
          <div className="text-center text-gray-500 py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="text-sm">您好！我是智能客服"大白"</p>
            <p className="text-xs text-gray-400 mt-1">有什么可以帮助您的吗？</p>
          </div>
        )}

        {state.messages.map((message, index) => (
          <ChatMessage
            key={`${message.id}-${index}`}
            message={message}
            isStreaming={state.isStreaming && index === state.messages.length - 1 && message.message_type === 'assistant'}
          />
        ))}

        {/* AI正在输入指示器（仅在非流式时显示） */}
        {state.isTyping && !state.isStreaming && (
          <div className="flex items-start space-x-2">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium">
              大白
            </div>
            <div className="bg-gray-100 rounded-lg px-3 py-2 max-w-xs">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* 输入区域 */}
      <ChatInput
        value={inputValue}
        onChange={setInputValue}
        onSend={handleSendMessage}
        disabled={state.isLoading || !state.session}
        placeholder={state.session ? '输入消息...' : '正在连接客服...'}
      />
    </div>
  );
};